import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles/createTheme";

export const useStyles = makeStyles((theme: Theme) => ({
    actions: {
        display: "flex",
        gap: theme.spacing(1),
    },
    // Titled-card pattern, restyled to the brand card look (soft diffuse shadow —
    // the old tight blue shadow rendered as a hard line above the table header).
    card: {
        background: `${theme.palette.primary.main} !important`,
        borderRadius: "10px !important",
        border: "1px solid rgba(6,35,63,0.10)",
        boxShadow: "0 14px 34px rgba(6,35,63,0.08) !important",
        "&::before": {
            content: "none !important",
        },
        "& .Mui-expanded": {
            minHeight: "0 !important",
        },
    },
    cardBody: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: `${theme.spacing(4)} ${theme.spacing(8)}`,
        position: "relative",
    },
    cardHeader: {
        alignItems: "center",
        display: "flex",
        justifyContent: "space-between",
        padding: "14px 32px 0 32px",
    },
    columnHeader: {
        color: theme.palette.primary.dark,
        "&.MuiTypography-root": {
            fontSize: "12px",
        },
    },
    // Right side of the card header: the active-only filter next to the Generate button.
    headerActions: {
        alignItems: "center",
        display: "flex",
        gap: theme.spacing(2),
    },
    filterCheckbox: {
        color: `${theme.palette.info.main} !important`,
        "&.Mui-checked": {
            color: `${theme.palette.info.main} !important`,
        },
        "& .MuiSvgIcon-root": {
            fontSize: 20,
        },
    },
    filterCheckboxLabel: {
        marginRight: 0,
        "& .MuiFormControlLabel-label": {
            color: theme.palette.info.main,
            fontFamily: "Source Sans 3",
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            textDecorationLine: "underline",
        },
    },
    detailsBox: {
        marginTop: "32px",
        padding: "0px 22px",
    },
    expiringSoon: {
        color: theme.palette.warning.main,
        fontWeight: 500,
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        minWidth: 360,
    },
    keyRow: {
        alignItems: "center",
        display: "flex",
        gap: theme.spacing(1),
    },
    // Top-level CBO page wrapper — clears the fixed 64px navbar (mirrors CorporateDashboard).
    mainWrapper: {
        backgroundColor: theme.palette.backgrounds.main,
        display: "flex",
        flexDirection: "column",
        marginTop: 64,
        minHeight: "calc(100vh - 64px)",
        textAlign: "left",
    },
    mainWrapperHeaderTitle: {
        color: theme.palette.info.main,
        fontFamily: "Source Sans 3",
        fontSize: "18px",
        fontWeight: 400,
    },
    // Padding for the MainModal body (it ships unpadded); 32px sides align with the header/footer.
    modalBody: {
        padding: "16px 32px 24px 32px",
    },
    noData: {
        "&> div": {
            height: "76px !important",
        },
    },
    notAvailable: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(4),
        textAlign: "center",
    },
    reveal: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        minWidth: 420,
    },
    // Shared input look for the modal fields (mirrors the Mill Settings `textField`).
    textField: {
        height: 56,
        textAlign: "left",
        width: "100%",
        "& .MuiOutlinedInput-root": {
            fontFamily: "Source Sans 3",
            fontSize: "16px",
            fontWeight: "400",
            "&.MuiOutlinedInput-input": {
                fontFamily: "Source Sans 3",
                fontSize: "16px",
                fontWeight: "400",
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.info.light,
            },
        },
        "& .MuiInputLabel-outlined": {
            fontFamily: "Source Sans 3",
            fontSize: "16px",
            fontWeight: "400",
            "&.Mui-focused": {
                color: theme.palette.info.light,
            },
        },
    },
    // CBO informational-tooltip look (mirrors MachineCenterCard / FiltersButton): a light,
    // card-toned surface with a subtle border and dark Open Sans text, instead of MUI's default
    // dark tooltip. Applied to the popper via PopperProps.className.
    tooltip: {
        marginBottom: `${theme.spacing(2)} !important`,
        "&.MuiTooltip-popper .MuiTooltip-tooltip": {
            backgroundColor: theme.palette.secondary.light,
            border: "1px solid rgba(193, 200, 218, 1)",
        },
    },
    tooltipTexts: {
        "&.MuiTypography-root": {
            color: theme.palette.text.primary,
            fontFamily: "Source Sans 3",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "left",
        },
    },
    // Utility: force the brand font onto MUI components (Button/Typography/Alert set their own
    // font, so a plain class can't win — !important is the house pattern, see MainModal.styles).
    fontFamily: {
        fontFamily: "Source Sans 3 !important",
    },
}));
